// The scope switcher at the top of the rail, built for agencies the way Google Analytics' account and property
// picker is: the project on the left, its sites on the right, with "All sites" first. Every view reads the choice: a
// section shows all of the project's sites, or one site.
//
// THE LEFT COLUMN IS YOUR PROJECTS (Settings › Workspace › Projects, Xano `projects`): All sites, each project, and the
// sites no project holds. Picking a project narrows the sites column, and every list of "all sites" under the shell,
// to that project's websites (lib/projectScope.ts, lib/projectScopeContext.tsx). It stays open: the project is the
// first half of the choice and the site is the second, so the search takes the keyboard.
//
// The data workspace (Tables, Interfaces, Studio) used to be this column. It is still its own choice (lib/scope.tsx),
// made in the section header on the screens that read it; a project can pin one, and picking that project picks it.
//
// Picking a site moves the current tab to that site's version of the same screen (lib/navMap.ts scopeTarget), so
// the address always says what you are looking at and a link to it opens the same scope for somebody else.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronsUpDown, FolderOpen, Inbox, Layers, LayoutGrid, Link2, Plus, Search } from "lucide-react";
import * as Pop from "@radix-ui/react-popover";
import { useProjectScope } from "@/lib/projectScopeContext";
import { choiceLabel, filterWebsites, projectCounts, type ProjectChoice } from "@/lib/projectScope";
import type { WebsiteRecord } from "@/lib/websites";

export function SiteTile({ site, size }: { site: Pick<WebsiteRecord, "name" | "color"> | null; size?: "sm" }) {
  const cls = `nv-tile${size ? ` ${size}` : ""}${site ? "" : " all"}`;
  if (!site) return <span aria-hidden className={cls}><LayoutGrid /></span>;
  return <span aria-hidden className={cls} style={site.color ? { background: site.color } : undefined}>{site.name.trim().charAt(0).toUpperCase() || "?"}</span>;
}

export function ScopeSwitcher({ site, websites, pending, failed, onPick, onPickProject, href, compact, open: openProp, onOpenChange }: {
  site: WebsiteRecord | null;
  /** Every website you can open: the switcher narrows them to the chosen project itself. */
  websites: WebsiteRecord[];
  pending: boolean;
  failed: boolean;
  onPick: (site: WebsiteRecord | null) => void;
  onPickProject: (choice: ProjectChoice) => void;
  /** Turns a path into an href (the /dev/nav harness mounts the app under a prefix). */
  href: (to: string) => string;
  compact?: boolean;
  /** Controlled from outside (the section header's site chip opens the rail's switcher). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const project = useProjectScope();
  const { choice, projects, filter, membership, current } = project;
  const [openOwn, setOpenOwn] = useState(false);
  const open = openProp ?? openOwn;
  const setOpen = (o: boolean) => { setOpenOwn(o); onOpenChange?.(o); };
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  const findRef = useRef<HTMLInputElement>(null);
  const pickProject = (c: ProjectChoice) => { onPickProject(c); findRef.current?.focus(); };
  // The counts beside each project need every project's websites: read once the switcher is first opened.
  const { wantAll } = project;
  useEffect(() => { if (open) wantAll(); }, [open, wantAll]);
  // On a phone the projects are a row that scrolls sideways: bring the current one into view.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const row = projectsRef.current, on = row?.querySelector<HTMLElement>('[aria-current="true"]');
      if (row && on && row.scrollWidth > row.clientWidth) row.scrollLeft = Math.max(0, on.offsetLeft - row.offsetLeft - 24);
    }, 0);
    return () => clearTimeout(t);
  }, [open, choice]);

  const scoped = useMemo(() => (filter === undefined ? [] : filterWebsites(websites, filter)), [websites, filter]);
  const counts = useMemo(() => projectCounts(websites, projects, membership), [websites, projects, membership]);
  const waiting = pending || filter === undefined;
  const needle = q.trim().toLowerCase();
  const sorted = useMemo(() => [...scoped].sort((a, b) => Number(a.status === "offline") - Number(b.status === "offline") || a.name.localeCompare(b.name)), [scoped]);
  const shown = needle ? sorted.filter((w) => w.name.toLowerCase().includes(needle) || (w.domain ?? "").toLowerCase().includes(needle)) : sorted;
  const options: (WebsiteRecord | null)[] = needle ? shown : [null, ...shown];
  useEffect(() => { setActive(0); }, [needle, open]);
  useEffect(() => { listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" }); }, [active]);

  const pick = (w: WebsiteRecord | null) => { setOpen(false); setQ(""); onPick(w); };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(options.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === "Enter" && options[active] !== undefined) { e.preventDefault(); pick(options[active]!); }
  };

  const groupLabel = choice === null ? "Every project" : choiceLabel(choice, projects);
  const siteLabel = site ? site.name : "All sites";
  const allSub = choice === null ? "Every site, side by side" : choice === "none" ? "Every site not in a project" : `Every site in ${current?.name ?? "this project"}`;
  const manage = typeof choice === "number" ? `/projects/${choice}` : "/projects";

  return (
    <Pop.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <Pop.Trigger asChild>
        <button type="button" className="nv-scope-btn" data-testid="scope-switcher" aria-label={`Project and site: ${groupLabel}, ${siteLabel}`}>
          <SiteTile site={site} />
          <span className="txt">
            {!compact && <small>{groupLabel}</small>}
            <b>{siteLabel}</b>
            {compact && <small>{groupLabel}</small>}
          </span>
          <ChevronsUpDown className="nv-updown" aria-hidden />
        </button>
      </Pop.Trigger>
      <Pop.Portal>
      <Pop.Content align="start" sideOffset={8} collisionPadding={8} className="nv-pop z-50" data-testid="scope-menu">
        <div className="nv-switch">
          <div className="nv-col ws" role="group" aria-label="Projects">
            <div className="nv-h"><span>Projects</span></div>
            <div className="nv-col-list" ref={projectsRef}>
              <ProjectItem on={choice === null} onClick={() => pickProject(null)} tile={<span aria-hidden className="nv-tile sm all"><Layers /></span>} label="All sites" n={pending || failed ? undefined : websites.length} />
              {projects.map((p) => (
                <ProjectItem key={p.id} on={choice === p.id} onClick={() => pickProject(p.id)} tile={<span aria-hidden className="nv-tile sm ws">{p.name.trim().charAt(0).toUpperCase() || "?"}</span>} label={p.name} n={counts.byProject.get(p.id) ?? p.site_count} />
              ))}
              {projects.length > 0 && (
                <ProjectItem on={choice === "none"} onClick={() => pickProject("none")} tile={<span aria-hidden className="nv-tile sm all"><Inbox /></span>} label="Not in a project" n={counts.none ?? undefined} />
              )}
              {project.loading && <div className="nv-empty" role="status">Loading your projects…</div>}
              {project.failed && <div className="nv-empty">Your projects couldn't be loaded. All sites still works.</div>}
            </div>
            <div className="nv-foot">
              <Link className="nv-item quiet" to={href(manage)} onClick={() => setOpen(false)} data-testid="manage-projects"><FolderOpen aria-hidden />{projects.length ? "Manage projects" : "Group sites into a project"}</Link>
            </div>
          </div>
          <div className="nv-col sites">
            <label className="nv-find">
              <Search aria-hidden />
              <input ref={findRef} autoFocus={!compact} placeholder="Find a site" aria-label="Find a site" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
                role="combobox" aria-expanded aria-controls="nv-site-list" aria-activedescendant={`nv-site-${active}`} />
            </label>
            <div className="nv-h"><span>Sites</span>{!waiting && !failed && <span>{scoped.length}</span>}</div>
            <div className="nv-col-list" id="nv-site-list" role="listbox" aria-label="Sites" ref={listRef}>
              {!needle && (
                <button type="button" role="option" id="nv-site-0" data-index={0} aria-selected={!site} data-active={active === 0} className="nv-item" onClick={() => pick(null)} data-testid="site-option-all">
                  <SiteTile site={null} size="sm" />
                  <span className="nm"><span>All sites</span><small>{allSub}</small></span>
                  {!site && <Check className="ck" aria-hidden />}
                </button>
              )}
              {waiting ? <div className="nv-empty">Loading your sites…</div> : failed ? <div className="nv-empty">Your sites couldn't be loaded. All sites still works.</div> : shown.map((w, i) => {
                const idx = needle ? i : i + 1;
                return (
                  <button type="button" role="option" key={w.ref} id={`nv-site-${idx}`} data-index={idx} aria-selected={site?.ref === w.ref} data-active={active === idx} className="nv-item" onClick={() => pick(w)} data-testid="site-option">
                    <SiteTile site={w} size="sm" />
                    <span className="nm"><span>{w.name}</span>{w.domain && <small>{w.domain}</small>}</span>
                    {w.status === "draft" ? <span className="tag">Draft</span> : w.status === "offline" ? <span className="tag">Off</span> : null}
                    {site?.ref === w.ref && <Check className="ck" aria-hidden />}
                  </button>
                );
              })}
              {!waiting && !failed && !needle && !shown.length && typeof choice === "number" && (
                <div className="nv-empty" data-testid="project-empty">No sites in {current?.name ?? "this project"} yet. <Link to={href(manage)} onClick={() => setOpen(false)} className="underline underline-offset-2">Add some</Link></div>
              )}
              {!waiting && project.membershipFailed && filter === null && choice !== null && <div className="nv-empty">This project's sites couldn't be read, so every site is listed.</div>}
              {needle && !waiting && !shown.length && <div className="nv-empty">No site matches “{q}”.</div>}
            </div>
            <div className="nv-foot row">
              <Link className="nv-item quiet" to={href("/sites/new")} onClick={() => setOpen(false)}><Plus aria-hidden />Add a website</Link>
              <Link className="nv-item quiet" to={href("/sites/connect")} onClick={() => setOpen(false)}><Link2 aria-hidden />Connect one</Link>
            </div>
          </div>
        </div>
      </Pop.Content>
      </Pop.Portal>
    </Pop.Root>
  );
}

function ProjectItem({ on, onClick, tile, label, n }: { on: boolean; onClick: () => void; tile: ReactNode; label: string; n?: number }) {
  return (
    <button type="button" className="nv-item" aria-current={on} onClick={onClick} data-testid="project-option">
      {tile}
      <span className="nm"><span>{label}</span></span>
      {n !== undefined && !on && <span className="n">{n}</span>}
      {on && <Check className="ck" aria-hidden />}
    </button>
  );
}
